<template>
  <div class="scroll-panel full-height">
    <div class="text-end">
      <v-btn class="mb-5" color="primary" @click="addGoal()"> Add goal </v-btn>
    </div>
    <v-card v-if="allGoals.length == 0" class="mb-5">
      <v-card-text>No active goals</v-card-text>
    </v-card>
    <v-row justify="center">
      <v-col class="mt-6">
        <v-card v-for="goal in allGoals" :key="goal.id" elevation="1" class="mb-3 mx-2">
          <goal :goal="goal" :editable="true" @edit="editGoal(goal)" @delete="deleteGoal(goal)"></goal>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="goalModal.show" width="400" transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar :color="timeframeColor(form.timeframe)">
          <v-toolbar-title> Goal </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="goalModal.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <ValidationObserver ref="obs" v-slot="{ invalid, handleSubmit }">
            <v-form ref="form" class="mt-6" @submit.prevent="handleSubmit(submit)">
              <ValidationProvider name="goal description" rules="required" v-slot="{ errors, valid }">
                <v-textarea
                  v-model="form.ultimateWhy"
                  rows="4"
                  auto-grow
                  label="Describe the goal"
                  :error-messages="errors"
                  :success="valid"
                >
                </v-textarea>
              </ValidationProvider>

              <v-select label="Goal status" v-model="form.status" :items="statuses"> </v-select>

              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.dueDate"
                    v-bind="attrs"
                    v-on="on"
                    label="Due Date"
                    readonly
                  ></v-text-field>
                </template>
                <v-date-picker v-model="form.dueDate" no-title scrollable @input="menu = false"> </v-date-picker>
              </v-menu>

              <v-menu
                ref="menu1"
                v-model="menu1"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.achievedAt"
                    v-bind="attrs"
                    v-on="on"
                    label="Achieved Date"
                    readonly
                  ></v-text-field>
                </template>
                <v-date-picker v-model="form.achievedAt" no-title scrollable @input="menu1 = false"> </v-date-picker>
              </v-menu>

              <v-menu
                ref="menu"
                v-model="menu2"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.createdAt"
                    v-bind="attrs"
                    v-on="on"
                    label="Created Date"
                    readonly
                  ></v-text-field>
                </template>
                <v-date-picker v-model="form.createdAt" no-title scrollable @input="menu2 = false"> </v-date-picker>
              </v-menu>

              <v-card-actions>
                <v-spacer />
                <v-btn color="secondary" type="submit" :disabled="invalid" :loading="buttonLoading">Save</v-btn>
              </v-card-actions>
            </v-form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  CLIENT_GOALS_QUERY,
  UPSERT_GOAL_MUTATION,
  ACTIVITIES_QUERY,
  DELETE_GOAL_MUTATION,
} from "@/constants/graphql/goal/goal";
import { GOAL_STRUCTURE } from "@/constants/json/goalStructure";
import { toLocalISOString } from "@/lib/timezone";
import { fancy_date } from "@/lib/fancy_date";
import GoalsBase from "./GoalsBase";
import Goal from "./Goal";

export default {
  name: "Goals",
  props: {
    userId: {
      type: String,
      default: null,
    },
  },
  components: { Goal },
  mixins: [GoalsBase],
  data() {
    return {
      loading: true,
      buttonLoading: false,
      form: {
        id: null,
        type: null,
        category: null,
        ultimateWhy: null,
        timeframe: null,
        status: "In progress",
        achievedAt: null,
        createdAt: null,
      },
      allGoals: [],
      defaultDate: null,
      menu: false,
      menu1: false,
      menu2: false,
      goalTypes: GOAL_STRUCTURE,
      goalTimeframes: [
        { text: "Big Hairy Audacious Goal", value: "BHAG" },
        { text: "Check-in", value: "CHECKIN" },
      ],
      statuses: ["In progress", "Achieved", "Closed", "Not achieved"],
      goalModal: {
        show: false,
        edit: false,
        date: null,
      },

      showCompleted: false,
      earnBadge: false,
    };
  },
  created() {
    this.defaultDate = this.$moment()
      .add(1, "M")
      .format("YYYY-MM-DD");
    this.goalModal.date = this.defaultDate;
  },
  computed: {
    dueDate: {
      get() {
        let date = this.$moment(this.goalModal.date);
        return `${date.format("ddd, D MMMM YYYY")}, ${date.fromNow()}`;
      },
      set(newValue) {
        this.goalModal.date = newValue;
      },
    },
    categories() {
      if (this.form.type) {
        return this.goalTypes
          .find((type) => type.value == this.form.type)
          ?.categories?.filter((category) => category.timeframe == this.form.timeframe || category.timeframe == null);
      }
      return [];
    },
    measurements() {
      if (this.form.category && this.categories) {
        return this.categories.find((category) => category.value == this.form.category)?.measurements;
      }
      return [];
    },
  },
  methods: {
    formatDate(date) {
      return `${this.$moment(date).format("ddd, D MMMM YYYY")}`;
    },
    resetForm() {
      this.$refs?.form?.reset();
      this.form.status = "In progress";
    },
    camelCase(value) {
      value = value.toLowerCase();
      value = value[0].toUpperCase() + value.substr(1);

      return value;
    },
    hasTextUnit(goal) {
      return goal.goalMeasurement.some((measurement) => measurement.targetText != null);
    },
    hasValueUnits(goal) {
      return goal.goalMeasurement.some(
        (measurement) => measurement.initialValue != null || measurement.targetValue != null
      );
    },
    closeBadgeEarning() {
      this.earnBadge = false;
    },
    closedTimeframeColor(timeframe, status) {
      let color = "grey";

      if (status == "Achieved") return this.timeframeColor(timeframe);

      switch (timeframe) {
        case "BHAG":
          color += " darken-1";
          break;
      }

      return color;
    },

    addGoal() {
      this.resetForm();
      this.goalModal = {
        edit: false,
        show: true,
        date: this.defaultDate,
      };
    },
    editGoal(goal) {
      goal.status = goal.status ? this.camelCase(goal.status.replace("_", " ")) : null;
      this.form = {
        id: goal.id,
        type: goal.type,
        category: goal.category,
        ultimateWhy: goal.ultimateWhy,
        timeframe: goal.timeframe,
        status: goal.status,
        measurements: goal.goalMeasurement,
        achievedAt: goal.achievedAt,
        createdAt: goal.createdAt,
        dueDate: goal.dueDate,
      };

      this.goalModal = {
        edit: true,
        show: true,
        date: this.$moment(goal.dueDate).format("YYYY-MM-DD"),
      };
    },
    deleteGoal(goal) {
      this.$apollo
        .mutate({
          mutation: DELETE_GOAL_MUTATION,
          variables: {
            clientGoalId: goal.id,
          },
        })
        .then((response) => {
          if (response.data) {
            this.$apollo.queries.clientGoals.refetch(this.userId);
          }
        });
    },

    submit() {
      this.buttonLoading = true;
      this.$refs.obs.validate();

      let goalInput = {
        id: this.form.id,
        userId: parseInt(this.userId),
        ultimateWhy: this.form.ultimateWhy,
        dueDate: this.form.dueDate,
        status: this.form.status ? this.form.status.replace("_", " ") : null,
        achievedAt: this.form.achievedAt,
        createdAt: this.form.createdAt,
      };

      let measurementInputs = [];
      // this.form.measurements.forEach((data) => {
      //   measurementInputs.push({
      //     id: data.id,
      //     initialValue: data.initialValue,
      //     targetValue: data.targetValue,
      //     unit: data.unit?.text ?? data.unit,
      //     targetText: data.targetText,
      //   });
      // });

      this.upsertGoal(goalInput, measurementInputs);
    },
    upsertGoal(goalInput, measurementInputs) {
      this.$apollo
        .mutate({
          mutation: UPSERT_GOAL_MUTATION,
          variables: {
            input: goalInput,
            //measurements: measurementInputs,
          },
        })
        .then((response) => {
          if (response.data) {
            this.buttonLoading = false;
            this.goalModal.show = false;

            // if (this.form.status == "Achieved") {
            //   this.earnBadge = true;
            // }

            this.resetForm();
            this.form.id = null;
            this.$apollo.queries.clientGoals.refetch(this.userId);
          }
        });
    },
  },
  apollo: {
    goalActivities: {
      query: ACTIVITIES_QUERY,
      fetchPolicy: "cache-and-network",
      update(data) {
        return data.goalActivities;
      },
    },
    clientGoals: {
      query: CLIENT_GOALS_QUERY,
      variables() {
        return {
          userId: this.userId,
        };
      },
      fetchPolicy: "no-cache",
      result(response) {
        if (response.data && !response.loading) {
          this.loading = false;
        }
      },
      update(data) {
        // data.clientGoals.forEach((goal) => {
        //   goal.type = goal.type ? this.camelCase(goal.type).replace("_", " ") : "";
        //   goal.status = goal.status ? this.camelCase(goal.status).replace("_", " ") : "";
        // });

        this.allGoals = data.clientGoals;

        // this.closedGoals = data.clientGoals.filter((goal) => goal.status != "In progress");
        // this.closedGoals.sort((a, b) =>
        //   (a.achievedAt ? a.achievedAt : a.dueDate) > (b.achievedAt ? b.achievedAt : b.dueDate) ? 1 : -1
        // );

        return data.clientGoals;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.floating-button {
  position: fixed;
  bottom: 25px;
  right: 15px;
  z-index: 1;
}

.v-expansion-panel-content > div.v-expansion-panel-content__wrap {
  background-color: black;
  padding: 0px;
}

.scroll-panel {
  display: inline-flex;
  flex-direction: column;
  position: relative;
  height: 95%;
  overflow-y: auto;
  overflow-x: hidden;
}

.full-height {
  height: 100%;
}
</style>
